import { LoyaltyProgram, User } from '../types';
import { userDataService } from './userDataService';

/**
 * Robust persistence service that handles user-specific data storage
 * with fallback from Firebase to localStorage
 */
export class PersistenceService {
  private static readonly GUEST_CARDS_KEY = 'guestCreditCards';
  private static readonly USER_CARDS_PREFIX = 'userCreditCards_';
  private static readonly USER_TRANSACTIONS_PREFIX = 'userTransactions_';

  /**
   * Generate a consistent user key based on user ID or email
   */
  private static getUserKey(user: User): string {
    // Use user ID if available, otherwise fall back to email
    const identifier = user.id || user.email || 'unknown';
    const cleanKey = identifier.replace(/[^a-zA-Z0-9]/g, '_'); // Sanitize for localStorage
    console.log('🔑 Generated user key:', cleanKey, 'from identifier:', identifier);
    return cleanKey;
  }

  /**
   * Load loyalty programs for a user
   */
  static async getUserLoyaltyPrograms(user: User | null): Promise<LoyaltyProgram[]> {
    console.log('═══════════════════════════════════════════');
    console.log('📥 PERSISTENCE: getUserLoyaltyPrograms');
    console.log('═══════════════════════════════════════════');
    console.log('👤 User:', user ? { id: user.id, email: user.email } : 'GUEST MODE');

    if (!user) {
      console.log('📱 Mode: GUEST - Loading from localStorage');
      const guestCards = JSON.parse(localStorage.getItem(this.GUEST_CARDS_KEY) || '[]');
      console.log('📱 Guest cards loaded:', guestCards.length, 'items');
      return guestCards;
    }

    const userKey = this.getUserKey(user);
    console.log('� User key:', userKey);

    // Try Firebase first if user has an ID and Firebase is configured
    if (user.id) {
      try {
        console.log('☁️  Attempting Firebase load...');
        // Check if Firebase is properly configured
        const { db } = await import('../config/firebase');
        if (!db) {
          throw new Error('Firebase not configured');
        }
        
        console.log('☁️  Firebase configured, calling userDataService...');
        const programs = await userDataService.getUserLoyaltyPrograms(user.id);
        console.log('☁️  Firebase response:', programs.length, 'total programs');
        console.log('☁️  Program types:', programs.map(p => ({ name: p.name, type: p.type })));
        
        // Firebase is working properly, use Firebase as primary source
        const localKey = `${this.USER_CARDS_PREFIX}${userKey}`;
        
        // Check if we have local programs that aren't in Firebase yet
        const existingLocal = JSON.parse(localStorage.getItem(localKey) || '[]');
        console.log('💾 Local storage check:', existingLocal.length, 'programs in localStorage');
        
        if (existingLocal.length > 0 && programs.length === 0) {
          console.log('⚠️  LOCAL HAS DATA, FIREBASE EMPTY - Syncing to Firebase...');
          // Sync local programs to Firebase
          for (const localProgram of existingLocal) {
            try {
              await userDataService.addUserLoyaltyProgram(user.id, localProgram);
              console.log('☁️  ✓ Synced:', localProgram.name);
            } catch (syncError) {
              console.warn('☁️  ✗ Sync failed:', localProgram.name, syncError);
            }
          }
          // Reload from Firebase after sync
          const updatedPrograms = await userDataService.getUserLoyaltyPrograms(user.id);
          localStorage.setItem(localKey, JSON.stringify(updatedPrograms));
          console.log('✅ After sync:', updatedPrograms.length, 'programs');
          return updatedPrograms;
        }
        
        // Use Firebase data and backup to localStorage
        localStorage.setItem(localKey, JSON.stringify(programs));
        console.log('💾 Backed up to localStorage');
        console.log('✅ Returning', programs.length, 'programs from Firebase');
        
        return programs;
      } catch (error: any) {
        console.warn('☁️  ❌ Firebase failed:', error?.message || error);
        console.log('↪️  Falling back to localStorage...');
      }
    }

    // Fallback to localStorage
    const localKey = `${this.USER_CARDS_PREFIX}${userKey}`;
    console.log('💾 Loading from localStorage with key:', localKey);
    
    const rawData = localStorage.getItem(localKey);
    console.log('💾 Raw data length:', rawData?.length || 0, 'characters');
    const allPrograms = JSON.parse(rawData || '[]');
    console.log('💾 Parsed programs:', allPrograms.length);
    console.log('💾 Program details:', allPrograms.map((p: LoyaltyProgram) => ({ 
      name: p.name, 
      type: p.type, 
      id: p.id 
    })));
    console.log('✅ Returning', allPrograms.length, 'programs from localStorage');
    return allPrograms;
  }

  /**
   * Save a new loyalty program for a user
   */
  static async addUserLoyaltyProgram(user: User | null, program: Omit<LoyaltyProgram, 'id'>): Promise<LoyaltyProgram> {
    console.log('═══════════════════════════════════════════');
    console.log('💾 PERSISTENCE: addUserLoyaltyProgram');
    console.log('═══════════════════════════════════════════');
    console.log('👤 User:', user ? { id: user.id, email: user.email } : 'GUEST MODE');
    console.log('� Program to add:', { 
      name: program.name, 
      type: program.type,
      cardNumber: (program as any).cardNumber,
      points: program.points 
    });

    const programWithId: LoyaltyProgram = {
      ...program,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 5), // Unique ID
    };
    console.log('🆔 Generated ID:', programWithId.id);

    if (!user) {
      console.log('📱 Mode: GUEST - Saving to localStorage');
      const guestCards = JSON.parse(localStorage.getItem(this.GUEST_CARDS_KEY) || '[]');
      console.log('📱 Existing guest cards:', guestCards.length);
      guestCards.push(programWithId);
      localStorage.setItem(this.GUEST_CARDS_KEY, JSON.stringify(guestCards));
      console.log('✅ Saved to guest storage. New total:', guestCards.length);
      return programWithId;
    }

    const userKey = this.getUserKey(user);
    console.log('� User key:', userKey);

    // Try Firebase first if user has an ID and Firebase is configured
    if (user.id) {
      try {
        console.log('☁️  Attempting Firebase save...');
        const firebaseId = await userDataService.addUserLoyaltyProgram(user.id, program);
        const firebaseProgram: LoyaltyProgram = { ...program, id: firebaseId };
        console.log('☁️  Firebase save SUCCESS:', { id: firebaseId, name: firebaseProgram.name });
        
        // Also save to localStorage as backup
        const localKey = `${this.USER_CARDS_PREFIX}${userKey}`;
        console.log('💾 Syncing to localStorage with key:', localKey);
        
        const existingCards = JSON.parse(localStorage.getItem(localKey) || '[]');
        console.log('💾 Existing programs in localStorage:', existingCards.length);
        
        existingCards.push(firebaseProgram);
        localStorage.setItem(localKey, JSON.stringify(existingCards));
        console.log('✅ Saved to localStorage. New total:', existingCards.length);
        console.log('✅ COMPLETE - Program saved to both Firebase and localStorage');
        
        return firebaseProgram;
      } catch (error: any) {
        console.warn('☁️  ❌ Firebase save failed:', error?.message || error);
        console.log('↪️  Falling back to localStorage only...');
      }
    }

    // Fallback to localStorage
    const localKey = `${this.USER_CARDS_PREFIX}${userKey}`;
    console.log('💾 Saving to localStorage with key:', localKey);
    const existingCards = JSON.parse(localStorage.getItem(localKey) || '[]');
    console.log('💾 Existing programs:', existingCards.length);
    
    existingCards.push(programWithId);
    const serializedData = JSON.stringify(existingCards);
    localStorage.setItem(localKey, serializedData);
    console.log('💾 Saved to localStorage. New total:', existingCards.length);
    console.log('💾 Data size:', serializedData.length, 'characters');
    
    // Verify the save worked
    const verification = localStorage.getItem(localKey);
    const verifiedPrograms = JSON.parse(verification || '[]');
    console.log('✅ Verification: Read back', verifiedPrograms.length, 'programs from localStorage');
    console.log('✅ COMPLETE - Program saved to localStorage only');
    
    return programWithId;
  }

  /**
   * Delete a loyalty program for a user
   */
  static async deleteUserLoyaltyProgram(user: User | null, programId: string): Promise<void> {
    console.log('🗑️ Deleting loyalty program:', programId, 'for user:', user);

    if (!user) {
      console.log('📱 Deleting from guest storage');
      const guestCards = JSON.parse(localStorage.getItem(this.GUEST_CARDS_KEY) || '[]');
      const updatedCards = guestCards.filter((card: LoyaltyProgram) => card.id !== programId);
      localStorage.setItem(this.GUEST_CARDS_KEY, JSON.stringify(updatedCards));
      console.log('📱 Guest card deleted. Remaining:', updatedCards.length);
      return;
    }

    const userKey = this.getUserKey(user);
    console.log('👤 Deleting for user key:', userKey);

    // Try Firebase first if user has an ID
    if (user.id) {
      try {
        console.log('☁️ Deleting from Firebase...');
        await userDataService.deleteUserLoyaltyProgram(programId);
        console.log('☁️ Firebase delete successful');
      } catch (error: any) {
        console.warn('☁️ Firebase delete failed:', error?.message || error);
      }
    }

    // Always also delete from localStorage
    const localKey = `${this.USER_CARDS_PREFIX}${userKey}`;
    const existingCards = JSON.parse(localStorage.getItem(localKey) || '[]');
    const updatedCards = existingCards.filter((card: LoyaltyProgram) => card.id !== programId);
    localStorage.setItem(localKey, JSON.stringify(updatedCards));
    console.log('💾 Deleted from localStorage. Remaining:', updatedCards.length);
  }

  /**
   * Update an existing loyalty program for a user
   */
  static async updateUserLoyaltyProgram(user: User | null, program: LoyaltyProgram): Promise<void> {
    console.log('🔄 Updating loyalty program:', program.id, 'for user:', user);

    if (!user) {
      console.log('📱 Updating in guest storage');
      const guestCards = JSON.parse(localStorage.getItem(this.GUEST_CARDS_KEY) || '[]');
      const updatedCards = guestCards.map((card: LoyaltyProgram) => 
        card.id === program.id ? program : card
      );
      localStorage.setItem(this.GUEST_CARDS_KEY, JSON.stringify(updatedCards));
      console.log('📱 Guest card updated');
      return;
    }

    const userKey = this.getUserKey(user);
    console.log('👤 Updating for user key:', userKey);

    // Try Firebase first if user has an ID
    if (user.id) {
      try {
        console.log('☁️ Attempting to update in Firebase...');
        await userDataService.updateUserLoyaltyProgram(user.id, program);
        console.log('☁️ Firebase update success');
      } catch (error) {
        console.warn('☁️ Firebase update failed:', error);
      }
    }

    // Always also update localStorage
    const localKey = `${this.USER_CARDS_PREFIX}${userKey}`;
    const existingCards = JSON.parse(localStorage.getItem(localKey) || '[]');
    const updatedCards = existingCards.map((card: LoyaltyProgram) => 
      card.id === program.id ? program : card
    );
    localStorage.setItem(localKey, JSON.stringify(updatedCards));
    console.log('💾 Updated in localStorage');
  }

  /**
   * Clear all data for a specific user (useful for logout)
   */
  static clearUserData(user: User | null): void {
    if (!user) {
      console.log('🧹 Clearing guest data');
      localStorage.removeItem(this.GUEST_CARDS_KEY);
      return;
    }

    const userKey = this.getUserKey(user);
    console.log('🧹 Clearing data for user key:', userKey);
    
    const localCardsKey = `${this.USER_CARDS_PREFIX}${userKey}`;
    const localTransactionsKey = `${this.USER_TRANSACTIONS_PREFIX}${userKey}`;
    
    localStorage.removeItem(localCardsKey);
    localStorage.removeItem(localTransactionsKey);
    console.log('🧹 User data cleared from localStorage');
  }

  /**
   * Get debug info about stored data
   */
  static getDebugInfo(user: User | null): any {
    const info: any = {
      user: user,
      timestamp: new Date().toISOString(),
    };

    // Get all localStorage keys for debugging
    const allKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      allKeys.push(localStorage.key(i));
    }
    info.allLocalStorageKeys = allKeys;

    if (!user) {
      const guestCards = JSON.parse(localStorage.getItem(this.GUEST_CARDS_KEY) || '[]');
      info.guestCards = guestCards.length;
      info.guestData = guestCards;
    } else {
      const userKey = this.getUserKey(user);
      const localCardsKey = `${this.USER_CARDS_PREFIX}${userKey}`;
      const userCards = JSON.parse(localStorage.getItem(localCardsKey) || '[]');
      
      info.userKey = userKey;
      info.localCardsKey = localCardsKey;
      info.userCards = userCards.length;
      info.userData = userCards;
      
      // Also check if data exists under the key
      info.rawLocalStorageValue = localStorage.getItem(localCardsKey);
    }

    return info;
  }

  /**
   * Debug function to log all persistence-related localStorage keys
   */
  static debugAllStorageKeys(): void {
    console.log('🔍 Checking persistence keys...');
    
    let foundKeys = 0;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('userCreditCards_') || key.includes('guestCreditCards'))) {
        foundKeys++;
        const value = localStorage.getItem(key);
        const parsed = JSON.parse(value || '[]');
        console.log(`🔍 ${key}: ${parsed.length} cards`);
      }
    }
    
    if (foundKeys === 0) {
      console.log('🔍 No persistence keys found');
    }
  }
}

export default PersistenceService;